import React from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { calculatePagination } from '@aboutbits/pagination'
import { useIntl } from 'react-intl'
import { translationsShared } from '../translations/translationsShared'
import {
  SectionPaginationContainer,
  SectionPaginationNextContent,
  SectionPaginationPagesList,
  SectionPaginationPagesListItem,
  SectionPaginationPreviousContent,
} from './SectionPagination'

const calculateLink = (
  routerQuery: string,
  page: number,
  size: number
): string => {
  const params = new URLSearchParams(routerQuery)

  params.set('page', String(page))
  params.set('size', String(size))

  const queryString = params.toString()

  return queryString === '' ? '' : '?' + queryString
}

const SectionPaginationRouter: React.FC<{
  page: number
  size: number
  total: number
}> = ({ page, size, total }) => {
  const intl = useIntl()
  const [searchParams] = useSearchParams()

  const pagination = calculatePagination(page, size, total)

  if (pagination === null) return null

  const enabledLink = 'hover:underline'
  const disabledLink = 'text-gray-500 cursor-not-allowed pointer-events-none'
  const currentLink = 'font-bold'

  return (
    <SectionPaginationContainer>
      <Link
        to={calculateLink(
          searchParams.toString(),
          pagination.previous.indexNumber,
          size
        )}
        aria-label={intl.formatMessage(translationsShared.paginationPrevious)}
        aria-disabled={pagination.previous.isDisabled}
        role="previous-link"
        className={classNames(
          'flex items-center',
          pagination.previous.isDisabled ? disabledLink : enabledLink
        )}
      >
        <SectionPaginationPreviousContent />
      </Link>

      <SectionPaginationPagesList>
        {pagination.pages.map((page) => {
          return (
            <SectionPaginationPagesListItem key={page.indexNumber}>
              <Link
                to={calculateLink(
                  searchParams.toString(),
                  page.indexNumber,
                  size
                )}
                aria-current={page.isCurrent ? 'page' : false}
                aria-label={intl.formatMessage(
                  translationsShared.paginationPage,
                  { page: page.displayNumber }
                )}
                className={classNames(
                  enabledLink,
                  page.isCurrent ? currentLink : ''
                )}
              >
                {page.displayNumber}
              </Link>
            </SectionPaginationPagesListItem>
          )
        })}
      </SectionPaginationPagesList>

      <Link
        to={calculateLink(
          searchParams.toString(),
          pagination.next.indexNumber,
          size
        )}
        aria-label={intl.formatMessage(translationsShared.paginationNext)}
        aria-disabled={pagination.next.isDisabled}
        role="next-link"
        className={classNames(
          'flex items-center',
          pagination.next.isDisabled ? disabledLink : enabledLink
        )}
      >
        <SectionPaginationNextContent />
      </Link>
    </SectionPaginationContainer>
  )
}

export { SectionPaginationRouter }
